import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Utensils,
  Store,
  ShoppingBag,
  MapPin,
  CheckCircle,
  Leaf,
  Heart,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { useFeaturedProducts } from '../../services/productService';
import { getImageUrl } from '../../lib/supabase';
import { formatPrice } from '../../lib/utils';

const AUTO_ROTATE_INTERVAL = 4000; // 4 seconds

/**
 * About Us Section — split layout with auto-rotating product showcase.
 * Left: About content | Right: Random product carousel
 */
export function AboutUs() {
  const { data: products, isLoading } = useFeaturedProducts();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-rotate through random products
  const rotateProduct = useCallback(() => {
    if (!products || products.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % products.length);
  }, [products]);

  useEffect(() => {
    if (!products || products.length <= 1) return;
    const timer = setInterval(rotateProduct, AUTO_ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [products, rotateProduct]);

  const currentProduct = products?.[currentIndex];
  const productImage = currentProduct?.image_path ? getImageUrl(currentProduct.image_path) : null;

  return (
    <section className="py-10 sm:py-14 bg-brand-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left — About Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Header */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Utensils size={24} className="text-brand-red" />
                <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-dark">
                  About Smothers Restaurant
                </h2>
              </div>
              <p className="text-lg font-bold text-brand-red">
                Authentic. Tasty. Truly African.
              </p>
            </div>

            {/* Main description */}
            <p className="text-sm sm:text-base text-brand-gray leading-relaxed">
              At Smothers Restaurant, we serve up the rich, vibrant flavors of Africa — fresh, hearty, and made with love. Our passion is to bring you authentic African cuisine that warms the heart and satisfies the soul. From the sizzling aroma of grilled Rump steak to the comfort of ugali, sukuma, and stews bursting with flavor — every meal is a taste of home.
            </p>

            {/* Dine Your Way */}
            <div className="bg-ui-background rounded-2xl p-5 space-y-4">
              <h3 className="text-base font-extrabold text-brand-dark flex items-center gap-2">
                <Store size={18} className="text-brand-red" />
                Dine Your Way
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-red/10 flex items-center justify-center flex-shrink-0">
                    <Utensils size={14} className="text-brand-red" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-brand-dark">Walk In & Dine</p>
                    <p className="text-xs text-brand-gray mt-0.5">
                      Take a seat in our warm, welcoming space and enjoy freshly cooked African dishes served with a smile.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-red/10 flex items-center justify-center flex-shrink-0">
                    <ShoppingBag size={14} className="text-brand-red" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-brand-dark">Order & Go</p>
                    <p className="text-xs text-brand-gray mt-0.5">
                      Short on time? Place your order ahead and pick it up when it's ready — quick, convenient, and delicious.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Locations */}
            <div className="space-y-3">
              <h3 className="text-base font-extrabold text-brand-dark flex items-center gap-2">
                <MapPin size={18} className="text-brand-red" />
                Our Nairobi Locations
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-brand-light rounded-xl p-4">
                  <p className="text-sm font-bold text-brand-dark">Imperial House</p>
                  <p className="text-xs text-brand-gray mt-1">Moi Avenue</p>
                  <p className="text-xs text-ui-muted mt-1">Perfect for lunch breaks or casual meetups</p>
                </div>
                <div className="bg-brand-light rounded-xl p-4">
                  <p className="text-sm font-bold text-brand-dark">Eagle House</p>
                  <p className="text-xs text-brand-gray mt-1">Kimathi Street</p>
                  <p className="text-xs text-ui-muted mt-1">A cozy city spot for food lovers on the go</p>
                </div>
              </div>
            </div>

            {/* Our Promise */}
            <div className="space-y-3">
              <h3 className="text-base font-extrabold text-brand-dark flex items-center gap-2">
                <Heart size={18} className="text-brand-red" />
                Our Promise
              </h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-brand-dark">Authentic Recipes</p>
                    <p className="text-xs text-brand-gray">We stay true to traditional African cooking — full of flavor, culture, and soul.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-brand-dark">Fresh Ingredients</p>
                    <p className="text-xs text-brand-gray">Sourced locally to ensure every dish is wholesome and satisfying.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-brand-dark">Friendly Service</p>
                    <p className="text-xs text-brand-gray">You're not just a customer — you're part of the Smothers family.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="flex items-center gap-3 px-4 py-3 bg-brand-red/5 rounded-xl border border-brand-red/20">
              <Clock size={20} className="text-brand-red flex-shrink-0" />
              <div>
                <p className="text-xs font-bold text-brand-dark">Open Daily</p>
                <p className="text-sm font-extrabold text-brand-red">6:00 AM – 10:00 PM</p>
              </div>
            </div>

            {/* CTA */}
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors shadow-lg group"
            >
              Explore Our Menu
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* Right — Auto-rotating Product Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            {isLoading ? (
              <div className="bg-brand-light rounded-2xl overflow-hidden">
                <div className="aspect-[4/5] animate-pulse" />
                <div className="p-6 space-y-3">
                  <div className="h-6 bg-brand-white rounded w-3/4 animate-pulse" />
                  <div className="h-4 bg-brand-white rounded w-1/2 animate-pulse" />
                  <div className="h-10 bg-brand-white rounded w-32 animate-pulse" />
                </div>
              </div>
            ) : currentProduct ? (
              <div className="bg-brand-white rounded-2xl border border-ui-border overflow-hidden shadow-lg">
                {/* Product Image */}
                <div className="relative overflow-hidden bg-brand-light">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentProduct.id}
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.6 }}
                      className="aspect-[4/5]"
                    >
                      {productImage ? (
                        <img
                          src={productImage}
                          alt={currentProduct.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Utensils size={60} className="text-ui-muted" />
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* Category badge */}
                  {currentProduct.categories?.name && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-brand-white/95 backdrop-blur-sm rounded-full">
                      <p className="text-xs font-bold text-brand-dark">
                        {currentProduct.categories.name}
                      </p>
                    </div>
                  )}

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 via-transparent to-transparent" />
                </div>

                {/* Product Info */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentProduct.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                    className="p-6 space-y-4"
                  >
                    <div>
                      <h3 className="text-xl font-extrabold text-brand-dark leading-tight">
                        {currentProduct.title}
                      </h3>
                      {currentProduct.short_description && (
                        <p className="text-sm text-brand-gray mt-2 line-clamp-2 leading-relaxed">
                          {currentProduct.short_description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="text-2xl font-extrabold text-brand-red">
                        {formatPrice(currentProduct.price)}
                      </p>
                      <Link
                        to={`/menu/${currentProduct.slug}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors"
                      >
                        View Details
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Rotation indicators */}
                {products && products.length > 1 && (
                  <div className="flex items-center justify-center gap-1.5 px-6 pb-5">
                    {products.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        className={`transition-all rounded-full ${
                          i === currentIndex
                            ? 'w-6 h-2 bg-brand-red'
                            : 'w-2 h-2 bg-ui-border hover:bg-brand-gray'
                        }`}
                        aria-label={`Go to product ${i + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-brand-light rounded-2xl p-12 text-center">
                <Utensils size={48} className="text-ui-muted mx-auto mb-3" />
                <p className="text-sm text-brand-gray">No featured products available</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
